// src/components/Common/ExportWithProgress.js
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";

function ExportWithProgress({
  open,
  onClose,
  filters,
  httpClient,
  startEndpoint,
  statusEndpoint,
  downloadEndpoint,
  onSuccess,
  onError,
  transformPayload,
}) {
  const [exportId, setExportId] = useState(null);
  const [status, setStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false); // Changed from polling to isPolling
  const [error, setError] = useState(null);

  const startExport = async () => {
    try {
      let payload = filters;
      if (transformPayload && typeof transformPayload === "function") {
        payload = transformPayload(filters);
      }

      console.log("=== START EXPORT ===");
      console.log("Endpoint:", startEndpoint);
      console.log("Payload:", payload);

      const response = await httpClient.post(startEndpoint, payload);
      console.log("Response:", response.data);

      const newExportId = response.data.exportId || response.data.id;
      console.log("Export ID:", newExportId);

      setExportId(newExportId);
      setStatus(response.data);

      // Start polling after we have the exportId
      setIsPolling(true);
    } catch (err) {
      console.error("Export start error:", err);
      const errorMsg = err.response?.data?.message || "Failed to start export";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    }
  };

  const checkStatus = async () => {
    if (!exportId) {
      console.log("No exportId yet, skipping status check");
      return;
    }

    try {
      const statusUrl = `${statusEndpoint}/${exportId}`;
      console.log("Checking status at:", statusUrl);

      const response = await httpClient.get(statusUrl);
      console.log("Status response:", response.data);
      console.log("Progress value:", response.data.progress);
      console.log("Processed records:", response.data.processedRecords);
      console.log("Total records:", response.data.totalRecords);

      setStatus(response.data);

      if (response.data.status === "COMPLETED") {
        console.log("Export completed, downloading...");
        setIsPolling(false);
        const downloadUrl = `${downloadEndpoint}/${exportId}`;
        await downloadFile(downloadUrl);
        if (onSuccess) onSuccess(response.data);
        setTimeout(() => onClose(), 3000);
      } else if (response.data.status === "FAILED") {
        console.log("Export failed:", response.data.errorMessage);
        setIsPolling(false);
        const errorMsg = response.data.errorMessage || "Export failed";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      } else {
        console.log(`Export status: ${response.data.status}, Progress: ${response.data.progress}%`);
      }
    } catch (err) {
      console.error("Status check error:", err);
      // Don't stop polling on transient errors
    }
  };

  const downloadFile = async (downloadUrl) => {
    try {
      console.log("Downloading from:", downloadUrl);
      const response = await httpClient.get(downloadUrl, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = `export_${exportId}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("File downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (open) {
      console.log("Dialog opened, starting export");
      startExport();
    } else {
      // Reset state when dialog closes
      setIsPolling(false);
      setExportId(null);
      setStatus(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (isPolling && exportId && open) {
      console.log("Starting polling for exportId:", exportId);
      const interval = setInterval(checkStatus, 4000);
      return () => {
        console.log("Clearing polling for exportId:", exportId);
        clearInterval(interval);
      };
    } else {
      console.log("Polling not started. Conditions:", { isPolling, exportId, open });
    }
  }, [isPolling, exportId, open]);

  const getStatusMessage = () => {
    if (!status) return "Starting export...";

    switch (status.status) {
      case "STARTING":
        return "Initializing export...";
      case "PENDING":
        return "Preparing export...";
      case "PROCESSING":
        const processed = status.processedRecords?.toLocaleString() || 0;
        const total = status.totalRecords?.toLocaleString() || 0;
        const percent = status.progress || 0;
        return `Processing: ${processed} of ${total} records (${percent}%)`;
      case "COMPLETED":
        return "Export complete! Downloading...";
      case "FAILED":
        return `Export failed: ${status.errorMessage}`;
      default:
        return "Processing...";
    }
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    if (seconds < 60) return `${Math.ceil(seconds)} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.ceil(seconds % 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ${remainingSeconds} second${
      remainingSeconds > 1 ? "s" : ""
    }`;
  };

  const handleClose = () => {
    console.log("Closing export dialog");
    setIsPolling(false);
    setExportId(null);
    setStatus(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <MDBox p={3} textAlign="center">
          {error ? (
            <>
              <Icon color="error" sx={{ fontSize: 48 }}>
                error
              </Icon>
              <MDTypography variant="h6" color="error" mt={2}>
                Export Failed
              </MDTypography>
              <MDTypography variant="body2" color="text" mt={1}>
                {error}
              </MDTypography>
              <MDButton variant="gradient" color="info" onClick={handleClose} mt={3}>
                Close
              </MDButton>
            </>
          ) : status?.status === "COMPLETED" ? (
            <>
              <Icon color="success" sx={{ fontSize: 48 }}>
                check_circle
              </Icon>
              <MDTypography variant="h6" color="success" mt={2}>
                Download Started!
              </MDTypography>
              <MDTypography variant="body2" color="text" mt={1}>
                Your file is being downloaded.
              </MDTypography>
            </>
          ) : (
            <>
              <Icon sx={{ fontSize: 48, color: "info.main" }}>file_download</Icon>
              <MDTypography variant="h6" mt={2}>
                Processing Export
              </MDTypography>
              <MDTypography variant="body2" color="text" mt={1}>
                {getStatusMessage()}
              </MDTypography>
              <MDBox mt={3}>
                <MDProgress
                  variant="determinate"
                  value={parseFloat(status?.progress) || 0}
                  color="info"
                />
              </MDBox>
              <MDTypography variant="caption" color="text" mt={1}>
                {status?.progress || 0}% complete
              </MDTypography>
              {status?.estimatedTimeRemaining > 0 && (
                <MDTypography variant="caption" color="text" mt={1} display="block">
                  Estimated time remaining: {formatTimeRemaining(status.estimatedTimeRemaining)}
                </MDTypography>
              )}
              {status?.processedRecords > 0 && status?.totalRecords > 0 && (
                <MDTypography variant="caption" color="text" mt={1} display="block">
                  {status.processedRecords.toLocaleString()} of{" "}
                  {status.totalRecords.toLocaleString()} records processed
                </MDTypography>
              )}
              <MDButton
                variant="text"
                color="secondary"
                onClick={handleClose}
                mt={3}
                disabled={status?.status === "PROCESSING" || status?.status === "STARTING"}
              >
                Cancel
              </MDButton>
            </>
          )}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

ExportWithProgress.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  httpClient: PropTypes.object.isRequired,
  startEndpoint: PropTypes.string.isRequired,
  statusEndpoint: PropTypes.string.isRequired,
  downloadEndpoint: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  transformPayload: PropTypes.func,
};

ExportWithProgress.defaultProps = {
  onSuccess: null,
  onError: null,
  transformPayload: null,
};

export default ExportWithProgress;
